import { useEffect, useState, useCallback } from "react";
import { Plus, MagnifyingGlass, Trash, Minus, DownloadSimple, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, MobileRow } from "@/components/Shared";
import { useCurrency } from "@/context/CurrencyContext";
import { COUNTRIES } from "@/lib/countries";
import { ImageCaptionField } from "@/components/ImageCaptionField";
import { MultiImageField } from "@/components/MultiImageField";
import { CaptionedImage, AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CATEGORIES = ["material", "consumable", "tool", "equipment"];
const EMPTY = {
  name: "", category: "material", sku: "", quantity: 0, unit: "pcs",
  reorder_level: 0, unit_cost: 0, currency: "", country: "", local_shipping_cost: 0,
  location: "", vendor_name: "", notes: "",
  image_id: null, image_caption: "", images: [],
};

export default function Inventory() {
  const { user } = useAuth();
  const { base_currency: baseCurrency, home_country: homeCountry, currencies, format } = useCurrency();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/items", {
        params: {
          category: tab, search,
          ...(countryFilter !== "all" ? { country: countryFilter } : {}),
        },
      });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [tab, search, countryFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/items", {
        ...form,
        quantity: Number(form.quantity),
        reorder_level: Number(form.reorder_level),
        unit_cost: Number(form.unit_cost),
        local_shipping_cost: Number(form.local_shipping_cost) || 0,
        currency: (form.currency || baseCurrency).toLowerCase(),
        country: (form.country || homeCountry || "US").toUpperCase(),
      });
      toast.success("Item added to inventory");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const adjust = async (id, delta) => {
    try {
      await api.post(`/items/${id}/adjust`, { delta, reason: "manual adjust" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/items/${id}`);
      toast.success("Item deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const advertise = async (id) => {
    try {
      await api.post(`/items/${id}/list`);
      toast.success("Advertised on the marketplace with its photos");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {    try {
      const res = await api.get("/items/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stock-handover-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Handover export downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="inventory-page">
      <PageHeader
        overline="Stock control"
        title="Inventory"
        description="Materials, consumable items, tools and equipment on the floor."
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="export-inventory-button"
              onClick={exportCsv}
              variant="outline"
              className="h-11 flex-1 rounded-none border-slate-300 text-xs font-semibold uppercase tracking-widest hover:bg-slate-100 sm:h-10 sm:flex-none"
            >
              <DownloadSimple size={16} className="mr-2" /> Export handover
            </Button>
            <Button
              data-testid="add-item-button"
              onClick={() => setOpen(true)}
              className="h-11 flex-1 rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:flex-none"
            >
              <Plus size={16} className="mr-2" /> Add item
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={tab} onValueChange={setTab} className="min-w-0">
          <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
            <TabsTrigger data-testid="filter-all" value="all" className="rounded-none text-xs uppercase tracking-wider">All</TabsTrigger>
            {CATEGORIES.map((c) => (
              <TabsTrigger key={c} data-testid={`filter-${c}`} value={c} className="rounded-none text-xs uppercase tracking-wider">
                {c}s
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger data-testid="inventory-country-filter" className="rounded-none border-slate-300 sm:w-48">
              <SelectValue placeholder="Every country" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="all">Every country</SelectItem>
              {COUNTRIES.map(([code, label]) => (
                <SelectItem key={code} value={code}>{code} — {label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative sm:w-72">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="inventory-search-input"
            placeholder="Search items…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-none border-slate-300 pl-9"
          />
          </div>
        </div>
      </div>

      {items.length > 0 && (
        <div data-testid="stock-value-strip" className="mb-4 flex flex-wrap gap-x-8 gap-y-2 border border-border bg-white px-4 py-3 text-xs">
          <span className="text-slate-500">
            Total stock value{" "}
            <span className="font-mono font-semibold text-[#0F172A]" data-testid="total-stock-value">
              {format(items.reduce((s, i) => s + Number(i.stock_value_base || 0), 0), baseCurrency)}
            </span>{" "}
            <span className="uppercase">{baseCurrency}</span>
          </span>
          <span className="text-slate-500">
            Local shipping on stock{" "}
            <span className="font-mono font-semibold text-[#0F172A]" data-testid="total-shipping-value">
              {format(items.reduce((s, i) => s + Number(i.local_shipping_base || 0) * Number(i.quantity || 0), 0), baseCurrency)}
            </span>
          </span>
          <span className="text-slate-500">
            Currencies in use{" "}
            <span className="font-semibold uppercase text-[#0F172A]" data-testid="currencies-in-use">
              {[...new Set(items.map((i) => i.currency))].join(", ")}
            </span>
          </span>
          <span className="text-slate-500">
            Countries{" "}
            <span className="font-semibold text-[#0F172A]" data-testid="countries-in-use">
              {[...new Set(items.map((i) => i.country))].join(", ")}
            </span>
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState testid="inventory-empty" title="No items yet" description="Add your first material, tool or piece of equipment." />
      ) : (
        <>
          <div className="border border-border bg-white md:hidden" data-testid="inventory-cards">
            {items.map((i) => {
              const low = Number(i.quantity) <= Number(i.reorder_level);
              return (
                <MobileRow
                  key={i.id}
                  testid={`item-card-${i.id}`}
                  title={i.name}
                  badges={
                    <>
                      <StatusBadge value={i.category} />
                      {low && <StatusBadge value="urgent" />}
                    </>
                  }
                  fields={[
                    { label: "On hand", value: `${i.quantity} ${i.unit}`, tone: low ? "danger" : undefined },
                    { label: "Reorder at", value: i.reorder_level },
                    { label: "Unit cost", value: `${format(i.unit_cost, i.currency)} ${(i.currency || "").toUpperCase()}` },
                    { label: `Stock value (${(baseCurrency || "usd").toUpperCase()})`, value: format(i.stock_value_base, baseCurrency) },
                    { label: "Sourced from", value: i.country || "—" },
                    { label: "Local shipping", value: i.local_shipping_cost ? `${format(i.local_shipping_cost, i.currency)} ${(i.currency || "").toUpperCase()}` : "—" },
                    { label: "Location", value: i.location || "—" },
                    { label: "Vendor", value: i.vendor_name || "—" },
                    { label: "SKU", value: i.sku || "—" },
                  ]}
                  media={
                    <div data-testid={`item-gallery-${i.id}`}>
                      <CaptionedImage
                        testid={`item-image-${i.id}`}
                        imageId={i.image_id}
                        caption={i.image_caption}
                        className="h-40 w-full border border-border object-cover"
                      />
                      {(i.images || []).length > 1 && (
                        <div className="mt-2 flex gap-2 overflow-x-auto">
                          {i.images.slice(1).map((img) => (
                            <AuthImage
                              key={img.image_id}
                              imageId={img.image_id}
                              alt={img.caption || i.name}
                              testid={`item-extra-${img.image_id}`}
                              className="h-14 w-14 shrink-0 border border-border object-cover"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  }
                  actions={
                    <>
                      <button
                        data-testid={`decrease-mobile-${i.id}`}
                        onClick={() => adjust(i.id, -1)}
                        aria-label="Decrease stock"
                        className="flex h-10 w-10 items-center justify-center border border-border transition-colors duration-150 active:bg-slate-100"
                      >
                        <Minus size={16} />
                      </button>
                      <button
                        data-testid={`increase-mobile-${i.id}`}
                        onClick={() => adjust(i.id, 1)}
                        aria-label="Increase stock"
                        className="flex h-10 w-10 items-center justify-center border border-border transition-colors duration-150 active:bg-slate-100"
                      >
                        <Plus size={16} />
                      </button>
                      {user?.role === "admin" && (
                        <button
                          data-testid={`delete-item-mobile-${i.id}`}
                          onClick={() => remove(i.id)}
                          aria-label="Delete item"
                          className="flex h-10 w-10 items-center justify-center border border-border text-[#FF3B30] transition-colors duration-150 active:bg-[#FFECEB]"
                        >
                          <Trash size={16} />
                        </button>
                      )}
                    </>
                  }
                />
              );
            })}
          </div>

          <div className="hidden overflow-x-auto border border-border bg-white md:block">
          <table className="w-full min-w-[840px] text-sm" data-testid="inventory-table">
            <thead>
              <tr className="border-b border-border bg-slate-50 text-left">
                {["Item", "Category", "SKU", "On hand", "Reorder", "Country", "Unit cost", `Stock value (${(baseCurrency || "usd").toUpperCase()})`, "Local shipping", "Location", "Vendor", ""].map((h) => (
                  <th key={h} className="overline px-3 py-2.5 text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const low = Number(i.quantity) <= Number(i.reorder_level);
                return (
                  <tr key={i.id} data-testid={`item-row-${i.id}`} className="border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-medium text-[#0F172A]">
                      <div className="flex items-center gap-2.5">
                        <CaptionedImage
                          testid={`item-thumb-${i.id}`}
                          imageId={i.image_id}
                          caption={null}
                          className="h-9 w-9 shrink-0 border border-border object-cover"
                        />
                        <span>
                          {i.name}
                          {(i.images || []).length > 1 && (
                            <span data-testid={`item-photo-count-${i.id}`} className="ml-1.5 border border-border px-1 text-[0.6rem] uppercase tracking-wider text-slate-500">
                              {i.images.length} photos
                            </span>
                          )}
                          {i.image_caption && (
                            <span
                              data-testid={`item-caption-${i.id}`}
                              className="block text-xs font-normal italic text-slate-500"
                            >
                              {i.image_caption}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5"><StatusBadge value={i.category} /></td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{i.sku || "—"}</td>
                    <td className={`px-3 py-2.5 font-mono ${low ? "font-bold text-[#FF3B30]" : "text-[#0F172A]"}`}>
                      {i.quantity} {i.unit}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{i.reorder_level}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500" data-testid={`item-country-${i.id}`}>{i.country}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500" data-testid={`item-cost-${i.id}`}>
                      {format(i.unit_cost, i.currency)} <span className="text-[0.65rem] uppercase">{i.currency}</span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500" data-testid={`item-value-base-${i.id}`}>
                      {format(i.stock_value_base, baseCurrency)}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-slate-500" data-testid={`item-shipping-${i.id}`}>
                      {i.local_shipping_cost ? format(i.local_shipping_cost, i.currency) : "—"}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{i.location || "—"}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-500">{i.vendor_name || "—"}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button data-testid={`decrease-${i.id}`} onClick={() => adjust(i.id, -1)} className="border border-border p-1.5 transition-colors duration-150 hover:bg-slate-100">
                          <Minus size={12} />
                        </button>
                        <button data-testid={`increase-${i.id}`} onClick={() => adjust(i.id, 1)} className="border border-border p-1.5 transition-colors duration-150 hover:bg-slate-100">
                          <Plus size={12} />
                        </button>
                        {(user?.role === "admin" || user?.role === "vendor") && (
                          <button
                            data-testid={`advertise-item-${i.id}`}
                            title="Advertise this stock on the marketplace"
                            onClick={() => advertise(i.id)}
                            className="border border-border p-1.5 text-[#002FA7] transition-colors duration-150 hover:bg-slate-100"
                          >
                            <Storefront size={12} />
                          </button>
                        )}
                        {user?.role === "admin" && (
                          <button data-testid={`delete-item-${i.id}`} onClick={() => remove(i.id)} className="border border-border p-1.5 text-[#FF3B30] transition-colors duration-150 hover:bg-[#FFECEB]">
                            <Trash size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Add inventory item</DialogTitle>
            <DialogDescription>Record a material, consumable, tool or piece of equipment.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="add-item-form">
            <div>
              <Label className="overline text-slate-500">Item name</Label>
              <Input data-testid="item-name-input" value={form.name} onChange={set("name")} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="item-category-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">SKU</Label>
                <Input data-testid="item-sku-input" value={form.sku} onChange={set("sku")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Quantity</Label>
                <Input data-testid="item-quantity-input" type="number" value={form.quantity} onChange={set("quantity")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Unit</Label>
                <Input data-testid="item-unit-input" value={form.unit} onChange={set("unit")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Reorder level</Label>
                <Input data-testid="item-reorder-input" type="number" value={form.reorder_level} onChange={set("reorder_level")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Unit cost</Label>
                <Input data-testid="item-cost-input" type="number" step="0.01" value={form.unit_cost} onChange={set("unit_cost")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Currency</Label>
                <Select value={form.currency || baseCurrency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="item-currency-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Object.entries(currencies || {}).map(([code, c]) => (
                      <SelectItem key={code} value={code}>{code.toUpperCase()} — {c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Sourced from (country)</Label>
                <Select value={form.country || homeCountry || "US"} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger data-testid="item-country-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {COUNTRIES.map(([code, label]) => (
                      <SelectItem key={code} value={code}>{code} — {label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Local shipping cost per unit</Label>
                <Input data-testid="item-shipping-input" type="number" step="0.01" value={form.local_shipping_cost} onChange={set("local_shipping_cost")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Location</Label>
                <Input data-testid="item-location-input" value={form.location} onChange={set("location")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Vendor</Label>
                <Input data-testid="item-vendor-input" value={form.vendor_name} onChange={set("vendor_name")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <MultiImageField
              testid="item-gallery"
              images={form.images}
              max={10}
              onChange={(images) => setForm((f) => ({ ...f, images }))}
            />
            <DialogFooter>
              <Button data-testid="save-item-button" type="submit" className="rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A]">
                Save item
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
